import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import { supabase } from "./client";
import { normalizeQrMenuStyle } from "../local/qrMenuStyle";
import { normalizeQrCategoryOrder, normalizeQrSubcategoryOrder } from "../local/qrCategoryOrder";
import {
  getCurrentAuthenticatedUser,
  sendInvitationSetupEmail,
} from "./authService";
import {
  DEFAULT_WAITER_PERMISSIONS,
  mapWaiterPermissionsFromDb,
  mapWaiterPermissionsToBranchPermissions,
  mapWaiterPermissionsToDb,
} from "../local/waiterPermissions";
import { buildInvitationPasswordSetupUrl } from "../local/invitationLinks";
import {
  DEFAULT_BRANCH_COMMISSION_RATE,
  DEFAULT_BRANCH_PAYMENT_PROVIDER,
  DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS,
  DEFAULT_BRANCH_PAYMENT_SPLIT_MODE,
  normalizeCommissionRate,
  normalizePaymentMethods,
  normalizePaymentProvider,
  normalizePaymentProviderStatus,
  normalizePaymentSplitMode,
} from "../local/paymentSettings";
import {
  buildMozzoRoleBranchPermissions,
  getMozzoInternalUserRole,
  resolveMozzoInternalUserRoleKey,
} from "../local/accessControl";
import { normalizeCompanyFiscalMode } from "../local/fiscalSettings";
import {
  buildBranchOperatingHoursRows,
  mapBranchOperatingHoursToSchedule,
} from "../local/branchOperatingHours";

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const normalizeError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  return new Error(error.message || fallbackMessage);
};

const normalizeNullableProfileText = (value) => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return "";
  }

  return `${value}`.trim();
};

const looksLikeMercadoPagoAppReference = (value) =>
  /^APP_USR-/i.test(
    value === undefined || value === null ? "" : `${value}`.trim()
  );

const isMissingRpcError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("function public.accept_my_pending_invitations")
  );
};

const isMissingUserProfilesTableError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes("user_profiles") ||
    message.includes("relation \"public.user_profiles\" does not exist")
  );
};

const isMissingWorkspaceRoleError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("workspace_role") ||
    message.includes("display_name") ||
    message.includes("waiter_workspace_patch") ||
    message.includes("kitchen_workspace_patch")
  );
};

const isMissingWaiterPermissionsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("waiter_can_take_orders") ||
    message.includes("waiter_can_modify_orders") ||
    message.includes("waiter_can_cancel_orders") ||
    message.includes("waiter_can_charge_orders") ||
    message.includes("waiter_can_refund_orders")
  );
};

const isMissingLimitedAdminVisibilityError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    error?.code === "PGRST204" ||
    message.includes("can_view_menu") ||
    message.includes("can_view_stock") ||
    message.includes("limited_admin_visibility_patch")
  );
};

const isMissingInternalUserRolesV1PatchError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "23514" ||
    message.includes("workspace_role") ||
    message.includes("admin_general") ||
    message.includes("encargado_sucursal") ||
    message.includes("soporte_mozzo") ||
    message.includes("caja")
  );
};

const buildInternalUserRolesV1PatchError = () =>
  new Error(
    "Falta aplicar personnel_hierarchy_v1_patch.sql en Supabase para usar encargado de sucursal y el flujo de Personal."
  );

const isMissingMenuAssignmentRpcError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("assign_shared_menu_to_branch")
  );
};

const isMissingBranchBrandingColumnsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("logo_compact_url") ||
    message.includes("icon_logo_url") ||
    message.includes("loading_logo_url") ||
    message.includes("kiosk_dine_in_image_url") ||
    message.includes("kiosk_takeaway_image_url") ||
    message.includes("qr_access_duration_minutes") ||
    message.includes("loading_animation") ||
    message.includes("payment_provider") ||
    message.includes("payment_provider_status") ||
    message.includes("payment_account_reference") ||
    message.includes("payment_account_cbu") ||
    message.includes("payment_account_alias") ||
    message.includes("payment_location_city") ||
    message.includes("payment_location_state") ||
    message.includes("payment_location_latitude") ||
    message.includes("payment_location_longitude") ||
    message.includes("payment_location_reference") ||
    message.includes("kiosk_native_qr_ready") ||
    message.includes("commission_rate") ||
    message.includes("payment_split_mode") ||
    message.includes("kiosk_payment_methods") ||
    message.includes("qr_payment_methods")
  );
};

const isMissingKioskColumnsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("service_mode") ||
    message.includes("kiosk_menu_mode") ||
    message.includes("kiosk_station_count")
  );
};

const isMissingCoverDisplayModeError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return error?.code === "42703" || message.includes("cover_display_mode");
};

const isMissingOperationalFeatureFlagsError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes("stock_enabled") ||
    message.includes("waiter_enabled") ||
    message.includes("ai_enabled") ||
    message.includes("salon_enabled") ||
    message.includes("delivery_enabled") ||
    message.includes("operational_setup_completed")
  );
};

const BRANCH_PUBLIC_SETTINGS_SELECT =
  "branch_id, logo_url, logo_compact_url, icon_logo_url, loading_logo_url, cover_url, cover_display_mode, kiosk_dine_in_image_url, kiosk_takeaway_image_url, primary_color, secondary_color, font_family, loading_animation, welcome_message, kitchen_mode, service_mode, salon_enabled, delivery_enabled, operational_setup_completed, stock_enabled, waiter_enabled, ai_enabled, kiosk_menu_mode, kiosk_station_count, qr_access_duration_minutes, payment_provider, payment_provider_status, payment_account_reference, payment_account_cbu, payment_account_alias, payment_location_city, payment_location_state, payment_location_latitude, payment_location_longitude, payment_location_reference, kiosk_native_qr_ready, commission_rate, payment_split_mode, kiosk_payment_methods, qr_payment_methods";

const BRANCH_PUBLIC_SETTINGS_OPERATIONAL_SELECT =
  "branch_id, logo_url, cover_url, cover_display_mode, primary_color, secondary_color, font_family, welcome_message, kitchen_mode, service_mode, salon_enabled, delivery_enabled, operational_setup_completed, stock_enabled, waiter_enabled, ai_enabled, kiosk_menu_mode, kiosk_station_count";

const BRANCH_PUBLIC_SETTINGS_KIOSK_SELECT =
  "branch_id, logo_url, cover_url, primary_color, secondary_color, font_family, welcome_message, kitchen_mode, service_mode, kiosk_menu_mode, kiosk_station_count";

const BRANCH_PUBLIC_SETTINGS_LEGACY_SELECT =
  "branch_id, logo_url, cover_url, primary_color, secondary_color, font_family, welcome_message, kitchen_mode";

const hasExtendedBranchBrandingPayload = (payload) =>
  Boolean(
    payload?.logoCompactUrl ||
      payload?.iconLogoUrl ||
      payload?.loadingLogoUrl ||
      payload?.kioskDineInImageUrl ||
      payload?.kioskTakeawayImageUrl ||
      (payload?.loadingAnimation && payload.loadingAnimation !== "spin")
  );

const hasOwnPayloadField = (payload, fieldName) =>
  Boolean(payload && Object.prototype.hasOwnProperty.call(payload, fieldName));

const normalizeKitchenMode = (value) =>
  value === "screen" || value === "printer" ? value : "manual";

const normalizeServiceMode = (value) => (value === "kiosk" ? "kiosk" : "salon");

const normalizeKioskMenuMode = (value) => (value === "custom" ? "custom" : "current");

const normalizeKioskStationCount = (value) =>
  Math.min(12, Math.max(1, Number(value || 1) || 1));

const normalizeCoverDisplayMode = (value) => {
  if (value === "centered" || value === "normal") {
    return value;
  }

  return "stretch";
};

const normalizeNullableText = (value, maxLength = 160) => {
  const normalized = `${value ?? ""}`.trim();
  return normalized ? normalized.slice(0, maxLength) : null;
};

const normalizeNullableCoordinate = (value, kind = "latitude") => {
  const raw = `${value ?? ""}`.trim();

  if (!raw) {
    return null;
  }

  const normalized = Number(raw.replace(",", "."));

  if (!Number.isFinite(normalized)) {
    throw new Error(
      kind === "latitude"
        ? "La latitud de la sucursal no es valida."
        : "La longitud de la sucursal no es valida."
    );
  }

  if (kind === "latitude" && (normalized < -90 || normalized > 90)) {
    throw new Error("La latitud de la sucursal debe estar entre -90 y 90.");
  }

  if (kind === "longitude" && (normalized < -180 || normalized > 180)) {
    throw new Error("La longitud de la sucursal debe estar entre -180 y 180.");
  }

  return Math.round(normalized * 1000000) / 1000000;
};

const ensureBranchSettingsSupportsKioskMode = async () => {
  const { error } = await supabase
    .from("branch_settings")
    .select("service_mode, kiosk_menu_mode, kiosk_station_count")
    .limit(1);

  if (error && isMissingKioskColumnsError(error)) {
    throw new Error(
      "Falta aplicar kiosk_mode_patch.sql en Supabase antes de guardar la modalidad fija de la sucursal."
    );
  }

  if (error) {
    throw normalizeError(
      error,
      "No se pudo validar la configuracion de modalidad de sucursales."
    );
  }
};

const getCurrentBranchOperationalSettings = async (branchId) => {
  const { data, error } = await supabase
    .from("branch_settings")
    .select(
      "kitchen_mode, service_mode, salon_enabled, delivery_enabled, operational_setup_completed, stock_enabled, waiter_enabled, ai_enabled, kiosk_menu_mode, kiosk_station_count"
    )
    .eq("branch_id", branchId)
    .maybeSingle();

  if (error) {
    if (
      isMissingKioskColumnsError(error) ||
      isMissingOperationalFeatureFlagsError(error)
    ) {
      return {};
    }

    throw normalizeError(
      error,
      "No se pudo leer la modalidad operativa actual de la sucursal."
    );
  }

  return {
    kitchenMode: data?.kitchen_mode,
    serviceMode: data?.service_mode,
    salonEnabled: data?.salon_enabled,
    deliveryEnabled: data?.delivery_enabled,
    operationalSetupCompleted: data?.operational_setup_completed,
    stockEnabled: data?.stock_enabled,
    waiterEnabled: data?.waiter_enabled,
    aiEnabled: data?.ai_enabled,
    kioskMenuMode: data?.kiosk_menu_mode,
    kioskStationCount: data?.kiosk_station_count,
  };
};

const ensureBranchSettingsSupportsBrandAssets = async () => {
  const { error } = await supabase
    .from("branch_settings")
    .select("logo_compact_url, icon_logo_url, loading_logo_url, loading_animation")
    .limit(1);

  if (error && isMissingBranchBrandingColumnsError(error)) {
    throw new Error(
      "Falta aplicar branch_brand_assets_patch.sql en Supabase antes de guardar mini logo, icono o logo de carga."
    );
  }

  if (error) {
    throw normalizeError(
      error,
      "No se pudo validar la configuracion visual avanzada de la sucursal."
    );
  }
};

const createSlug = (name, fallback = "item", suffix = "") => {
  const base = (name || fallback)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || fallback;

  return suffix ? `${base}-${suffix}` : base;
};

const createInvitationToken = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `inv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const pickInternalUserRoleKey = (payload = {}) => {
  if (payload.roleKey) {
    return payload.roleKey;
  }

  if (payload.workspaceRole) {
    return resolveMozzoInternalUserRoleKey({
      workspaceRole: payload.workspaceRole,
    });
  }

  if (payload.accessType === "kitchen") {
    return "cocina";
  }

  return "admin_limitado";
};

const buildInternalUserAccessPayload = (
  payload = {},
  { requireEmail = true, allowLegacyRole = false } = {}
) => {
  const roleKey = pickInternalUserRoleKey(payload);
  const roleDefinition = getMozzoInternalUserRole(roleKey);

  if (
    !roleDefinition ||
    roleDefinition.key === "owner" ||
    (roleDefinition.assignable === false &&
      !(allowLegacyRole && roleDefinition.legacy === true))
  ) {
    throw new Error("Ese rol todavia no se puede asignar desde la interfaz.");
  }

  const email = `${payload.email || ""}`.trim().toLowerCase();

  if (requireEmail && !email) {
    throw new Error("El correo del usuario interno es obligatorio.");
  }

  const branchId =
    roleDefinition.scope === "branch" ? `${payload.branchId || ""}`.trim() : "";

  if (roleDefinition.scope === "branch" && !branchId) {
    throw new Error("Necesitas asignar una sucursal al usuario.");
  }

  const branchPermissions = buildMozzoRoleBranchPermissions(roleKey, payload);

  return {
    roleKey,
    roleDefinition,
    email,
    branchId,
    displayName: `${payload.fullName || payload.displayName || ""}`.trim(),
    branchPermissions,
  };
};

const mapBranchPermissionsToColumns = (branchPermissions = {}) => ({
  can_view_menu: branchPermissions.canViewMenu === true,
  can_manage_menu: branchPermissions.canManageMenu === true,
  can_view_stock: branchPermissions.canViewStock === true,
  can_manage_stock: branchPermissions.canManageStock === true,
  can_manage_orders: branchPermissions.canManageOrders === true,
  can_manage_qr: branchPermissions.canManageQr === true,
  can_manage_payments: branchPermissions.canManagePayments === true,
  can_manage_settings: branchPermissions.canManageSettings === true,
  waiter_can_take_orders: branchPermissions.waiterCanTakeOrders === true,
  waiter_can_modify_orders: branchPermissions.waiterCanModifyOrders === true,
  waiter_can_cancel_orders: branchPermissions.waiterCanCancelOrders === true,
  waiter_can_charge_orders: branchPermissions.waiterCanChargeOrders === true,
  waiter_can_refund_orders: branchPermissions.waiterCanRefundOrders === true,
});

const normalizeBranchPermissions = (row = {}) => ({
  canViewMenu: row.can_view_menu === true,
  canManageMenu: row.can_manage_menu === true,
  canViewStock: row.can_view_stock === true,
  canManageStock: row.can_manage_stock === true,
  canManageOrders: row.can_manage_orders === true,
  canManageQr: row.can_manage_qr === true,
  canManagePayments: row.can_manage_payments === true,
  canManageSettings: row.can_manage_settings === true,
  waiterCanTakeOrders: row.waiter_can_take_orders === true,
  waiterCanModifyOrders: row.waiter_can_modify_orders === true,
  waiterCanCancelOrders: row.waiter_can_cancel_orders === true,
  waiterCanChargeOrders: row.waiter_can_charge_orders === true,
  waiterCanRefundOrders: row.waiter_can_refund_orders === true,
});

const deliverInvitationSetupEmail = async (invitation) => {
  const baseUrl =
    typeof window !== "undefined" ? window.location.origin.replace(/\/$/, "") : "";

  if (!baseUrl) {
    return {
      invitation_email_sent: false,
      invitation_email_error:
        "La invitacion quedo creada, pero no pudimos detectar la URL base para enviar el correo.",
    };
  }

  try {
    await sendInvitationSetupEmail({
      email: invitation.email,
      redirectTo: buildInvitationPasswordSetupUrl(invitation, baseUrl),
      userData: {
        invitation_token: invitation.invitation_token || "",
        company_id: invitation.company_id || "",
        branch_id: invitation.branch_id || "",
        workspace_role:
          invitation.workspace_role || invitation.workspaceRole || invitation.role || "",
      },
    });

    return {
      invitation_email_sent: true,
      invitation_email_error: "",
    };
  } catch (error) {
    return {
      invitation_email_sent: false,
      invitation_email_error:
        error.message ||
        "La invitacion quedo creada, pero no se pudo enviar el correo de acceso.",
    };
  }
};

const normalizeCompanyProfile = (company, settings) => ({
  ownerUserId: company?.owner_user_id || null,
  companyName: company?.name || "",
  legalName: settings?.legal_name || company?.legal_name || "",
  companyPhone: company?.phone || "",
  billingEmail: settings?.billing_email || "",
  supportPhone: settings?.support_phone || "",
  headquartersAddress: settings?.headquarters_address || "",
  taxId: settings?.tax_id || "",
  fiscalMode: normalizeCompanyFiscalMode(settings?.fiscal_mode),
  ownerFullName: settings?.owner_full_name || "",
  ownerDocumentId: settings?.owner_document_id || "",
  ownerPhone: settings?.owner_phone || "",
  ownerEmail: settings?.owner_email || "",
  bankAccountHolder: settings?.bank_account_holder || "",
  bankName: settings?.bank_name || "",
  bankAccountType: settings?.bank_account_type || "",
  bankAccountNumber: settings?.bank_account_number || "",
  bankCbu: settings?.bank_cbu || "",
  bankAlias: settings?.bank_alias || "",
  currency: settings?.currency || "ARS",
  timezone: settings?.timezone || "America/Argentina/Buenos_Aires",
});

const normalizeUserProfile = (profile, fallbackEmail = "") => ({
  fullName: profile?.full_name || "",
  documentId: profile?.document_id || "",
  phone: profile?.phone || "",
  email: fallbackEmail || "",
});

const normalizeBranchPublicProfile = (branch, settings, operatingHours = []) => ({
  qrMenuStyle: settings?.qr_menu_style === undefined
    ? undefined
    : normalizeQrMenuStyle(settings.qr_menu_style),
  qrCategoryOrder: settings?.qr_category_order === undefined
    ? undefined : normalizeQrCategoryOrder(settings.qr_category_order),
  qrSubcategoryOrder: settings?.qr_subcategory_order === undefined
    ? undefined : normalizeQrSubcategoryOrder(settings.qr_subcategory_order),
  name: branch?.name || "",
  phone: branch?.phone || "",
  address: branch?.address || "",
  description: branch?.description || "",
  horarioDetallado:
    mapBranchOperatingHoursToSchedule(operatingHours) || undefined,
  logoUrl: settings?.logo_url || settings?.logo_compact_url || undefined,
  logoCompactUrl: settings?.logo_compact_url || settings?.logo_url || undefined,
  iconLogoUrl:
    settings?.icon_logo_url ||
    settings?.logo_compact_url ||
    settings?.logo_url ||
    undefined,
  loadingLogoUrl:
    settings?.loading_logo_url ||
    settings?.icon_logo_url ||
    settings?.logo_compact_url ||
    settings?.logo_url ||
    undefined,
  coverUrl: settings?.cover_url || undefined,
  kioskDineInImageUrl: settings?.kiosk_dine_in_image_url || undefined,
  kioskTakeawayImageUrl: settings?.kiosk_takeaway_image_url || undefined,
  coverDisplayMode: settings?.cover_display_mode
    ? normalizeCoverDisplayMode(settings.cover_display_mode)
    : undefined,
  primaryColor: settings?.primary_color || undefined,
  secondaryColor: settings?.secondary_color || undefined,
  fontFamily: settings?.font_family || undefined,
  loadingAnimation:
    settings?.loading_animation === "pulse" || settings?.loading_animation === "float"
      ? settings.loading_animation
      : undefined,
  welcomeMessage: settings?.welcome_message || undefined,
  kitchenMode:
    settings?.kitchen_mode === "screen" || settings?.kitchen_mode === "printer"
      ? settings.kitchen_mode
      : undefined,
  serviceMode:
    typeof settings?.service_mode === "string"
      ? settings.service_mode === "kiosk"
        ? "kiosk"
        : "salon"
      : undefined,
  salonEnabled:
    settings?.salon_enabled === undefined || settings?.salon_enabled === null
      ? settings?.service_mode !== "kiosk"
      : settings.salon_enabled !== false,
  deliveryEnabled: settings?.delivery_enabled === true,
  operationalSetupCompleted: settings?.operational_setup_completed === true,
  stockEnabled: settings?.stock_enabled !== false,
  waiterEnabled: settings?.waiter_enabled !== false,
  aiEnabled: settings?.ai_enabled !== false,
  kioskMenuMode:
    typeof settings?.kiosk_menu_mode === "string"
      ? settings.kiosk_menu_mode === "custom"
        ? "custom"
        : "current"
      : undefined,
  kioskStationCount:
    settings?.kiosk_station_count === undefined || settings?.kiosk_station_count === null
      ? undefined
      : Math.min(12, Math.max(1, Number(settings.kiosk_station_count || 1) || 1)),
  qrAccessDurationMinutes:
    settings?.qr_access_duration_minutes === undefined ||
    settings?.qr_access_duration_minutes === null
      ? undefined
      : Math.min(
          720,
          Math.max(15, Number(settings.qr_access_duration_minutes || 120) || 120)
        ),
  paymentProvider:
    settings?.payment_provider === undefined || settings?.payment_provider === null
      ? DEFAULT_BRANCH_PAYMENT_PROVIDER
      : normalizePaymentProvider(settings.payment_provider),
  paymentProviderStatus:
    settings?.payment_provider_status === undefined ||
    settings?.payment_provider_status === null
      ? DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS
      : normalizePaymentProviderStatus(settings.payment_provider_status),
  paymentAccountReference: looksLikeMercadoPagoAppReference(
    settings?.payment_account_reference
  )
    ? ""
    : normalizeNullableProfileText(settings?.payment_account_reference),
  paymentAccountCbu: normalizeNullableProfileText(settings?.payment_account_cbu),
  paymentAccountAlias: normalizeNullableProfileText(
    settings?.payment_account_alias
  ),
  paymentLocationCity: normalizeNullableProfileText(
    settings?.payment_location_city
  ),
  paymentLocationState: normalizeNullableProfileText(
    settings?.payment_location_state
  ),
  paymentLocationLatitude:
    settings?.payment_location_latitude === undefined ||
    settings?.payment_location_latitude === null
      ? normalizeNullableProfileText(settings?.payment_location_latitude)
      : `${settings.payment_location_latitude}`,
  paymentLocationLongitude:
    settings?.payment_location_longitude === undefined ||
    settings?.payment_location_longitude === null
      ? normalizeNullableProfileText(settings?.payment_location_longitude)
      : `${settings.payment_location_longitude}`,
  paymentLocationReference: normalizeNullableProfileText(
    settings?.payment_location_reference
  ),
  kioskNativeQrReady: settings?.kiosk_native_qr_ready === true,
  commissionRate:
    settings?.commission_rate === undefined || settings?.commission_rate === null
      ? DEFAULT_BRANCH_COMMISSION_RATE
      : normalizeCommissionRate(settings.commission_rate),
  paymentSplitMode:
    settings?.payment_split_mode === undefined || settings?.payment_split_mode === null
      ? DEFAULT_BRANCH_PAYMENT_SPLIT_MODE
      : normalizePaymentSplitMode(settings.payment_split_mode),
  kioskPaymentMethods: normalizePaymentMethods(settings?.kiosk_payment_methods, "kiosk"),
  qrPaymentMethods: normalizePaymentMethods(settings?.qr_payment_methods, "qr"),
});

export const getCompanyProfile = async (companyId) => {
  ensureSupabaseReady();

  if (!companyId) {
    throw new Error("No encontramos una empresa activa para cargar el perfil.");
  }

  const [{ data: company, error: companyError }, { data: settings, error: settingsError }] =
    await Promise.all([
      supabase.from("companies").select("*").eq("id", companyId).single(),
      supabase.from("company_settings").select("*").eq("company_id", companyId).single(),
    ]);

  if (companyError) {
    throw normalizeError(companyError, "No se pudo cargar la empresa.");
  }

  if (settingsError) {
    throw normalizeError(settingsError, "No se pudo cargar la informacion de cobro.");
  }

  return normalizeCompanyProfile(company, settings);
};

export const updateCompanyProfile = async (companyId, payload) => {
  ensureSupabaseReady();

  if (!companyId) {
    throw new Error("No encontramos una empresa activa para actualizar.");
  }

  const { error: companyError } = await supabase
    .from("companies")
    .update({
      name: payload.companyName?.trim() || null,
      legal_name: payload.legalName?.trim() || null,
      phone: payload.companyPhone?.trim() || null,
    })
    .eq("id", companyId);

  if (companyError) {
    throw normalizeError(companyError, "No se pudo actualizar la empresa.");
  }

  const { error: settingsError } = await supabase.from("company_settings").upsert({
    company_id: companyId,
    billing_email: payload.billingEmail?.trim() || payload.ownerEmail?.trim() || null,
    support_phone: payload.supportPhone?.trim() || payload.companyPhone?.trim() || null,
    headquarters_address: payload.headquartersAddress?.trim() || null,
    legal_name: payload.legalName?.trim() || null,
    tax_id: payload.taxId?.trim() || null,
    fiscal_mode: normalizeCompanyFiscalMode(payload.fiscalMode),
    owner_full_name: payload.ownerFullName?.trim() || null,
    owner_document_id: payload.ownerDocumentId?.trim() || null,
    owner_phone: payload.ownerPhone?.trim() || null,
    owner_email: payload.ownerEmail?.trim() || null,
    bank_account_holder:
      payload.bankAccountHolder?.trim() || payload.ownerFullName?.trim() || null,
    bank_name: payload.bankName?.trim() || null,
    bank_account_type: payload.bankAccountType?.trim() || null,
    bank_account_number: payload.bankAccountNumber?.trim() || null,
    bank_cbu: payload.bankCbu?.trim() || null,
    bank_alias: payload.bankAlias?.trim() || null,
    currency: payload.currency?.trim() || "ARS",
    timezone: payload.timezone?.trim() || "America/Argentina/Buenos_Aires",
  });

  if (settingsError) {
    throw normalizeError(settingsError, "No se pudo actualizar la informacion del titular.");
  }

  return getCompanyProfile(companyId);
};

export const getCurrentUserProfile = async () => {
  ensureSupabaseReady();

  const user = await getCurrentAuthenticatedUser();

  if (!user?.id) {
    throw new Error("No encontramos una sesion activa para cargar el perfil.");
  }

  const { data, error } = await supabase
    .from("user_profiles")
    .select("*")
    .eq("user_id", user.id)
    .maybeSingle();

  if (error) {
    if (isMissingUserProfilesTableError(error)) {
      return normalizeUserProfile(null, user.email || "");
    }

    throw normalizeError(error, "No se pudo cargar el perfil personal.");
  }

  return normalizeUserProfile(data, user.email || "");
};

export const upsertCurrentUserProfile = async (payload) => {
  ensureSupabaseReady();

  const user = await getCurrentAuthenticatedUser();

  if (!user?.id) {
    throw new Error("No encontramos una sesion activa para guardar el perfil.");
  }

  const { error } = await supabase.from("user_profiles").upsert({
    user_id: user.id,
    full_name: payload.fullName?.trim() || null,
    document_id: payload.documentId?.trim() || null,
    phone: payload.phone?.trim() || null,
  });

  if (error) {
    if (isMissingUserProfilesTableError(error)) {
      throw new Error(
        "Falta aplicar el patch limited_admin_profiles_patch.sql en Supabase para guardar perfiles personales."
      );
    }

    throw normalizeError(error, "No se pudo guardar el perfil personal.");
  }

  return getCurrentUserProfile();
};

export const getBranchPublicProfile = async (branchId) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos una sucursal activa para cargar su perfil.");
  }

  const [
    { data: branch, error: branchError },
    initialSettingsResult,
    { data: operatingHours, error: operatingHoursError },
  ] =
    await Promise.all([
      supabase
        .from("branches")
        .select("id, name, phone, address, description")
        .eq("id", branchId)
        .single(),
      supabase
        .from("branch_settings")
        .select(`${BRANCH_PUBLIC_SETTINGS_SELECT}, qr_menu_style, qr_category_order, qr_subcategory_order`)
        .eq("branch_id", branchId)
        .maybeSingle(),
      supabase
        .from("branch_operating_hours")
        .select(
          "day_of_week, is_open, first_open, first_close, second_open, second_close"
        )
        .eq("branch_id", branchId)
        .order("day_of_week", { ascending: true }),
    ]);

  let settings = initialSettingsResult.data;
  let settingsError = initialSettingsResult.error;

  if (branchError) {
    throw normalizeError(branchError, "No se pudo cargar el perfil publico de la sucursal.");
  }

  if (operatingHoursError) {
    throw normalizeError(
      operatingHoursError,
      "No se pudo cargar el horario de atención de la sucursal."
    );
  }

  if (settingsError) {
    if (
      `${settingsError.message || ""}`.includes("qr_menu_style") ||
      `${settingsError.message || ""}`.includes("qr_category_order") ||
      `${settingsError.message || ""}`.includes("qr_subcategory_order") ||
      isMissingCoverDisplayModeError(settingsError) ||
      isMissingBranchBrandingColumnsError(settingsError) ||
      isMissingKioskColumnsError(settingsError) ||
      isMissingOperationalFeatureFlagsError(settingsError)
    ) {
      const settingsSelects = [
        BRANCH_PUBLIC_SETTINGS_SELECT,
        BRANCH_PUBLIC_SETTINGS_OPERATIONAL_SELECT,
        BRANCH_PUBLIC_SETTINGS_KIOSK_SELECT,
        BRANCH_PUBLIC_SETTINGS_LEGACY_SELECT,
      ];
      const selectCandidates = [
        ...settingsSelects.slice(1).map((fields) => `${fields}, qr_menu_style, qr_category_order, qr_subcategory_order`),
        ...settingsSelects.map((fields) => `${fields}, qr_menu_style, qr_category_order`),
        ...settingsSelects.map((fields) => `${fields}, qr_menu_style, qr_subcategory_order`),
        ...settingsSelects.map((fields) => `${fields}, qr_menu_style`),
        ...settingsSelects,
      ];

      let fallbackSettings = null;
      let fallbackError = settingsError;

      for (const selectClause of selectCandidates) {
        const result = await supabase
          .from("branch_settings")
          .select(selectClause)
          .eq("branch_id", branchId)
          .maybeSingle();

        fallbackSettings = result.data;
        fallbackError = result.error;

        if (!fallbackError) {
          break;
        }

        if (
          !`${fallbackError.message || ""}`.includes("qr_menu_style") &&
          !`${fallbackError.message || ""}`.includes("qr_category_order") &&
          !`${fallbackError.message || ""}`.includes("qr_subcategory_order") &&
          !isMissingCoverDisplayModeError(fallbackError) &&
          !isMissingBranchBrandingColumnsError(fallbackError) &&
          !isMissingKioskColumnsError(fallbackError) &&
          !isMissingOperationalFeatureFlagsError(fallbackError)
        ) {
          break;
        }
      }

      if (fallbackError) {
        throw normalizeError(
          fallbackError,
          "No se pudo cargar la configuracion visible de la sucursal."
        );
      }

      return normalizeBranchPublicProfile(
        branch,
        fallbackSettings,
        operatingHours
      );
    }

    throw normalizeError(
      settingsError,
      "No se pudo cargar la configuracion visible de la sucursal."
    );
  }

  return normalizeBranchPublicProfile(branch, settings, operatingHours);
};

export const updateBranchPublicProfile = async (branchId, payload) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos una sucursal activa para actualizar.");
  }

  if (!payload.name?.trim()) {
    throw new Error("El nombre del restaurante es obligatorio.");
  }

  if (hasExtendedBranchBrandingPayload(payload)) {
    await ensureBranchSettingsSupportsBrandAssets();
  }

  const [currentPublicProfile, currentOperationalSettings] = await Promise.all([
    getBranchPublicProfile(branchId),
    getCurrentBranchOperationalSettings(branchId),
  ]);

  const kitchenModeSource = hasOwnPayloadField(payload, "kitchenMode")
    ? payload.kitchenMode
    : currentOperationalSettings?.kitchenMode ?? currentPublicProfile?.kitchenMode;
  const serviceModeSource = hasOwnPayloadField(payload, "serviceMode")
    ? payload.serviceMode
    : currentOperationalSettings?.serviceMode ?? currentPublicProfile?.serviceMode;
  const kioskMenuModeSource = hasOwnPayloadField(payload, "kioskMenuMode")
    ? payload.kioskMenuMode
    : currentOperationalSettings?.kioskMenuMode ?? currentPublicProfile?.kioskMenuMode;
  const kioskStationCountSource = hasOwnPayloadField(payload, "kioskStationCount")
    ? payload.kioskStationCount
    : currentOperationalSettings?.kioskStationCount ?? currentPublicProfile?.kioskStationCount;
  const nextSalonEnabled = hasOwnPayloadField(payload, "salonEnabled")
    ? payload.salonEnabled !== false
    : currentOperationalSettings?.salonEnabled !== false;
  const nextDeliveryEnabled = hasOwnPayloadField(payload, "deliveryEnabled")
    ? payload.deliveryEnabled === true
    : currentOperationalSettings?.deliveryEnabled === true;
  const nextOperationalSetupCompleted = hasOwnPayloadField(
    payload,
    "operationalSetupCompleted"
  )
    ? payload.operationalSetupCompleted === true
    : currentOperationalSettings?.operationalSetupCompleted === true;
  const nextStockEnabled = hasOwnPayloadField(payload, "stockEnabled")
    ? payload.stockEnabled !== false
    : currentOperationalSettings?.stockEnabled !== false;
  const nextWaiterEnabled = hasOwnPayloadField(payload, "waiterEnabled")
    ? nextSalonEnabled && payload.waiterEnabled !== false
    : nextSalonEnabled && currentOperationalSettings?.waiterEnabled !== false;
  const nextAiEnabled = hasOwnPayloadField(payload, "aiEnabled")
    ? payload.aiEnabled !== false
    : currentOperationalSettings?.aiEnabled !== false;

  const nextKitchenMode =
    kitchenModeSource === undefined || kitchenModeSource === null || `${kitchenModeSource}`.trim() === ""
      ? null
      : normalizeKitchenMode(kitchenModeSource);
  const nextServiceMode =
    serviceModeSource === undefined || serviceModeSource === null || `${serviceModeSource}`.trim() === ""
      ? null
      : normalizeServiceMode(serviceModeSource);
  const nextKioskMenuMode =
    kioskMenuModeSource === undefined ||
    kioskMenuModeSource === null ||
    `${kioskMenuModeSource}`.trim() === ""
      ? null
      : normalizeKioskMenuMode(kioskMenuModeSource);
  const nextKioskStationCount =
    kioskStationCountSource === undefined || kioskStationCountSource === null
      ? null
      : normalizeKioskStationCount(kioskStationCountSource);
  const nextPaymentLocationLatitude = hasOwnPayloadField(
    payload,
    "paymentLocationLatitude"
  )
    ? normalizeNullableCoordinate(payload.paymentLocationLatitude, "latitude")
    : undefined;
  const nextPaymentLocationLongitude = hasOwnPayloadField(
    payload,
    "paymentLocationLongitude"
  )
    ? normalizeNullableCoordinate(payload.paymentLocationLongitude, "longitude")
    : undefined;
  const nextPaymentProvider = hasOwnPayloadField(payload, "paymentProvider")
    ? normalizePaymentProvider(payload.paymentProvider)
    : normalizePaymentProvider(currentPublicProfile?.paymentProvider);
  const nextPaymentProviderStatus = hasOwnPayloadField(payload, "paymentProviderStatus")
    ? normalizePaymentProviderStatus(payload.paymentProviderStatus)
    : normalizePaymentProviderStatus(currentPublicProfile?.paymentProviderStatus);
  const nextPaymentAccountReference = hasOwnPayloadField(payload, "paymentAccountReference")
    ? normalizeNullableText(payload.paymentAccountReference, 180)
    : undefined;
  const canPersistPaymentAccountReference =
    nextPaymentAccountReference !== undefined &&
    nextPaymentProvider === "manual" &&
    nextPaymentProviderStatus !== "connected" &&
    !looksLikeMercadoPagoAppReference(nextPaymentAccountReference);

  const { error: branchError } = await supabase
    .from("branches")
    .update({
      name: payload.name.trim(),
      phone: payload.phone?.trim() || null,
      address: payload.address?.trim() || null,
      description: payload.description?.trim() || null,
    })
    .eq("id", branchId);

  if (branchError) {
    throw normalizeError(branchError, "No se pudo actualizar la sucursal.");
  }

  if (hasOwnPayloadField(payload, "horarioDetallado")) {
    const { error: operatingHoursError } = await supabase
      .from("branch_operating_hours")
      .upsert(
        buildBranchOperatingHoursRows(branchId, payload.horarioDetallado),
        { onConflict: "branch_id,day_of_week" }
      );

    if (operatingHoursError) {
      throw normalizeError(
        operatingHoursError,
        "No se pudo sincronizar el horario de atención de la sucursal."
      );
    }
  }

  const { error: settingsError } = await supabase.from("branch_settings").upsert({
    branch_id: branchId,
    ...(hasOwnPayloadField(payload, "qrMenuStyle")
      ? { qr_menu_style: normalizeQrMenuStyle(payload.qrMenuStyle) }
      : {}),
    ...(hasOwnPayloadField(payload, "qrCategoryOrder")
      ? { qr_category_order: normalizeQrCategoryOrder(payload.qrCategoryOrder) }
      : {}),
    ...(hasOwnPayloadField(payload, "qrSubcategoryOrder")
      ? { qr_subcategory_order: normalizeQrSubcategoryOrder(payload.qrSubcategoryOrder) }
      : {}),
    logo_url: payload.logoUrl || null,
    logo_compact_url: payload.logoCompactUrl || null,
    icon_logo_url: payload.iconLogoUrl || null,
    loading_logo_url: payload.loadingLogoUrl || null,
    cover_url: payload.coverUrl || null,
    kiosk_dine_in_image_url: payload.kioskDineInImageUrl || null,
    kiosk_takeaway_image_url: payload.kioskTakeawayImageUrl || null,
    cover_display_mode: normalizeCoverDisplayMode(payload.coverDisplayMode),
    primary_color: payload.primaryColor?.trim() || "#2563eb",
    secondary_color: payload.secondaryColor?.trim() || "#f8fafc",
    font_family: payload.fontFamily?.trim() || null,
    loading_animation:
      payload.loadingAnimation === "pulse" || payload.loadingAnimation === "float"
        ? payload.loadingAnimation
        : "spin",
    welcome_message: payload.welcomeMessage?.trim() || null,
    ...(nextKitchenMode ? { kitchen_mode: nextKitchenMode } : {}),
    ...(nextServiceMode ? { service_mode: nextServiceMode } : {}),
    ...(hasOwnPayloadField(payload, "salonEnabled")
      ? { salon_enabled: nextSalonEnabled }
      : {}),
    ...(hasOwnPayloadField(payload, "deliveryEnabled")
      ? { delivery_enabled: nextDeliveryEnabled }
      : {}),
    ...(hasOwnPayloadField(payload, "operationalSetupCompleted")
      ? { operational_setup_completed: nextOperationalSetupCompleted }
      : {}),
    ...(hasOwnPayloadField(payload, "stockEnabled")
      ? { stock_enabled: nextStockEnabled }
      : {}),
    ...(hasOwnPayloadField(payload, "waiterEnabled") ||
    hasOwnPayloadField(payload, "salonEnabled")
      ? { waiter_enabled: nextWaiterEnabled }
      : {}),
    ...(hasOwnPayloadField(payload, "aiEnabled")
      ? { ai_enabled: nextAiEnabled }
      : {}),
    ...(nextKioskMenuMode ? { kiosk_menu_mode: nextKioskMenuMode } : {}),
    ...(nextKioskStationCount ? { kiosk_station_count: nextKioskStationCount } : {}),
    ...(hasOwnPayloadField(payload, "paymentProvider")
      ? { payment_provider: nextPaymentProvider }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentProviderStatus")
      ? {
          payment_provider_status: nextPaymentProviderStatus,
        }
      : {}),
    ...(canPersistPaymentAccountReference
      ? { payment_account_reference: nextPaymentAccountReference }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentAccountCbu")
      ? { payment_account_cbu: payload.paymentAccountCbu?.trim() || null }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentAccountAlias")
      ? { payment_account_alias: payload.paymentAccountAlias?.trim() || null }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentLocationCity")
      ? {
          payment_location_city: normalizeNullableText(
            payload.paymentLocationCity,
            120
          ),
        }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentLocationState")
      ? {
          payment_location_state: normalizeNullableText(
            payload.paymentLocationState,
            120
          ),
        }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentLocationLatitude")
      ? { payment_location_latitude: nextPaymentLocationLatitude }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentLocationLongitude")
      ? { payment_location_longitude: nextPaymentLocationLongitude }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentLocationReference")
      ? {
          payment_location_reference: normalizeNullableText(
            payload.paymentLocationReference,
            240
          ),
        }
      : {}),
    ...(hasOwnPayloadField(payload, "kioskNativeQrReady")
      ? { kiosk_native_qr_ready: payload.kioskNativeQrReady === true }
      : {}),
    ...(hasOwnPayloadField(payload, "commissionRate")
      ? { commission_rate: normalizeCommissionRate(payload.commissionRate) }
      : {}),
    ...(hasOwnPayloadField(payload, "paymentSplitMode")
      ? { payment_split_mode: normalizePaymentSplitMode(payload.paymentSplitMode) }
      : {}),
    ...(hasOwnPayloadField(payload, "kioskPaymentMethods")
      ? {
          kiosk_payment_methods: normalizePaymentMethods(
            payload.kioskPaymentMethods,
            "kiosk"
          ),
        }
      : {}),
    ...(hasOwnPayloadField(payload, "qrPaymentMethods")
      ? {
          qr_payment_methods: normalizePaymentMethods(payload.qrPaymentMethods, "qr"),
        }
      : {}),
  });

  if (settingsError) {
    if (`${settingsError.message || ""}`.includes("qr_menu_style")) {
      throw new Error("No se pudo guardar el estilo del menu. Falta actualizar la configuracion de la base de datos.");
    }
    if (`${settingsError.message || ""}`.includes("qr_category_order")) {
      throw new Error("No se pudo guardar el orden de categorias. Falta actualizar la configuracion de la base de datos.");
    }
    if (`${settingsError.message || ""}`.includes("qr_subcategory_order")) {
      throw new Error("No se pudo guardar el orden de subcategorias. Falta actualizar la configuracion de la base de datos.");
    }
    if (
      isMissingCoverDisplayModeError(settingsError) ||
      isMissingBranchBrandingColumnsError(settingsError) ||
      isMissingKioskColumnsError(settingsError) ||
      isMissingOperationalFeatureFlagsError(settingsError)
    ) {
      const legacySettingsPayload = {
        branch_id: branchId,
        ...(hasOwnPayloadField(payload, "qrMenuStyle")
          ? { qr_menu_style: normalizeQrMenuStyle(payload.qrMenuStyle) }
          : {}),
        ...(hasOwnPayloadField(payload, "qrCategoryOrder")
          ? { qr_category_order: normalizeQrCategoryOrder(payload.qrCategoryOrder) }
          : {}),
        ...(hasOwnPayloadField(payload, "qrSubcategoryOrder")
          ? { qr_subcategory_order: normalizeQrSubcategoryOrder(payload.qrSubcategoryOrder) }
          : {}),
        logo_url: payload.logoUrl || null,
        cover_url: payload.coverUrl || null,
        primary_color: payload.primaryColor?.trim() || "#2563eb",
        secondary_color: payload.secondaryColor?.trim() || "#f8fafc",
        font_family: payload.fontFamily?.trim() || null,
        welcome_message: payload.welcomeMessage?.trim() || null,
        ...(nextKitchenMode ? { kitchen_mode: nextKitchenMode } : {}),
      };

      if (!isMissingKioskColumnsError(settingsError)) {
        if (nextServiceMode) {
          legacySettingsPayload.service_mode = nextServiceMode;
        }
        if (nextKioskMenuMode) {
          legacySettingsPayload.kiosk_menu_mode = nextKioskMenuMode;
        }
        if (nextKioskStationCount) {
          legacySettingsPayload.kiosk_station_count = nextKioskStationCount;
        }
      }

      const { error: legacySettingsError } = await supabase
        .from("branch_settings")
        .upsert(legacySettingsPayload);

      if (legacySettingsError) {
        throw normalizeError(
          legacySettingsError,
          "No se pudo actualizar la portada y el mensaje visible de la sucursal."
        );
      }

      if (
        isMissingBranchBrandingColumnsError(settingsError) &&
        hasExtendedBranchBrandingPayload(payload)
      ) {
        throw new Error(
          "Guardamos la marca principal, pero falta aplicar el patch branch_brand_assets_patch.sql en Supabase para sincronizar mini logo, icono, logo de carga e imagenes del ingreso kiosk."
        );
      }

      if (isMissingCoverDisplayModeError(settingsError)) {
        throw new Error(
          "Guardamos la portada, pero falta aplicar el patch branch_cover_display_mode_patch.sql en Supabase para sincronizar el modo de ajuste de la imagen."
        );
      }

      if (
        isMissingOperationalFeatureFlagsError(settingsError) &&
        (hasOwnPayloadField(payload, "stockEnabled") ||
          hasOwnPayloadField(payload, "waiterEnabled") ||
          hasOwnPayloadField(payload, "aiEnabled"))
      ) {
        throw new Error(
          "Guardamos la sucursal, pero falta aplicar branch_operational_feature_flags_patch.sql en Supabase para sincronizar stock, mozo e IA por restaurante."
        );
      }

      return getBranchPublicProfile(branchId);
    }

    throw normalizeError(
      settingsError,
      "No se pudo actualizar la portada y el mensaje visible de la sucursal."
    );
  }

  return getBranchPublicProfile(branchId);
};

export const updateBranchKitchenMode = async (branchId, kitchenMode) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos una sucursal activa para actualizar.");
  }

  const currentPublicProfile = await getBranchPublicProfile(branchId);
  const nextMode = normalizeKitchenMode(kitchenMode);

  const settingsPayload = {
    branch_id: branchId,
    kitchen_mode: nextMode,
  };

  if (currentPublicProfile?.serviceMode) {
    settingsPayload.service_mode = normalizeServiceMode(currentPublicProfile.serviceMode);
  }

  if (currentPublicProfile?.kioskMenuMode) {
    settingsPayload.kiosk_menu_mode = normalizeKioskMenuMode(
      currentPublicProfile.kioskMenuMode
    );
  }

  if (currentPublicProfile?.kioskStationCount !== undefined) {
    settingsPayload.kiosk_station_count = normalizeKioskStationCount(
      currentPublicProfile.kioskStationCount
    );
  }

  const { error } = await supabase.from("branch_settings").upsert(settingsPayload);

  if (error) {
    if (isMissingKioskColumnsError(error)) {
      const { error: legacyError } = await supabase.from("branch_settings").upsert({
        branch_id: branchId,
        kitchen_mode: nextMode,
      });

      if (legacyError) {
        throw normalizeError(
          legacyError,
          "No se pudo actualizar el modo de operacion de cocina."
        );
      }

      return nextMode;
    }

    throw normalizeError(
      error,
      "No se pudo actualizar el modo de operacion de cocina."
    );
  }

  return nextMode;
};

export const updateBranchKioskConfig = async (
  branchId,
  {
    serviceMode = "salon",
    kioskMenuMode = "current",
    kioskStationCount = 1,
  } = {}
) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos una sucursal activa para actualizar.");
  }

  await ensureBranchSettingsSupportsKioskMode();

  const currentPublicProfile = await getBranchPublicProfile(branchId);
  const nextServiceMode = normalizeServiceMode(serviceMode);
  const nextKioskMenuMode = normalizeKioskMenuMode(kioskMenuMode);
  const nextKioskStationCount = normalizeKioskStationCount(kioskStationCount);

  const settingsPayload = {
    branch_id: branchId,
    service_mode: nextServiceMode,
    kiosk_menu_mode: nextKioskMenuMode,
    kiosk_station_count: nextKioskStationCount,
  };

  if (currentPublicProfile?.kitchenMode) {
    settingsPayload.kitchen_mode = normalizeKitchenMode(currentPublicProfile.kitchenMode);
  }

  const { error } = await supabase.from("branch_settings").upsert(settingsPayload);

  if (error) {
    if (isMissingKioskColumnsError(error)) {
      throw new Error(
        "La base todavia no tiene la configuracion de kiosk. Falta aplicar el patch kiosk_mode_patch.sql en Supabase."
      );
    }

    throw normalizeError(error, "No se pudo actualizar la configuracion de kiosk.");
  }

  return {
    serviceMode: nextServiceMode,
    kioskMenuMode: nextKioskMenuMode,
    kioskStationCount: nextKioskStationCount,
  };
};

export const updateBranchOperationalModules = async (branchId, payload = {}) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos una sucursal activa para actualizar.");
  }

  const currentSettings = await getCurrentBranchOperationalSettings(branchId);
  const serviceMode = normalizeServiceMode(
    payload.serviceMode ?? currentSettings.serviceMode
  );
  const salonEnabled =
    serviceMode === "kiosk" ? false : payload.salonEnabled !== false;
  const deliveryEnabled = payload.deliveryEnabled === true;
  const waiterEnabled = salonEnabled && payload.waiterEnabled !== false;
  const stockEnabled = payload.stockEnabled !== false;
  const aiEnabled = payload.aiEnabled !== false;
  const wePedidosEnabled =
    deliveryEnabled && payload.wePedidosEnabled === true;

  if (serviceMode !== "kiosk" && !salonEnabled && !deliveryEnabled) {
    throw new Error(
      "Activa al menos Salon o Delivery para que la sucursal tenga un canal de venta."
    );
  }

  const { error: settingsError } = await supabase.from("branch_settings").upsert({
    branch_id: branchId,
    service_mode: serviceMode,
    salon_enabled: salonEnabled,
    delivery_enabled: deliveryEnabled,
    operational_setup_completed: true,
    stock_enabled: stockEnabled,
    waiter_enabled: waiterEnabled,
    ai_enabled: aiEnabled,
  });

  if (settingsError) {
    throw normalizeError(
      settingsError,
      "No se pudieron guardar los modulos operativos de la sucursal."
    );
  }

  const { error: weSettingsError } = await supabase.from("we_branch_settings").upsert({
    branch_id: branchId,
    enabled: wePedidosEnabled,
    updated_at: new Date().toISOString(),
  });

  if (weSettingsError) {
    throw normalizeError(
      weSettingsError,
      "Guardamos los modulos, pero no pudimos sincronizar We! pedidos."
    );
  }

  return {
    serviceMode,
    salonEnabled,
    deliveryEnabled,
    operationalSetupCompleted: true,
    stockEnabled,
    waiterEnabled,
    aiEnabled,
    wePedidosEnabled,
  };
};

export const createBranch = async (companyId, payload) => {
  ensureSupabaseReady();

  if (!companyId) {
    throw new Error("No encontramos una empresa activa para crear la sucursal.");
  }

  if (!payload.name?.trim()) {
    throw new Error("El nombre de la sucursal es obligatorio.");
  }

  await ensureBranchSettingsSupportsKioskMode();

  const nextServiceMode = payload.serviceMode === "kiosk" ? "kiosk" : "salon";
  const nextKioskMenuMode = payload.kioskMenuMode === "custom" ? "custom" : "current";
  const nextKioskStationCount = Math.min(
    12,
    Math.max(1, Number(payload.kioskStationCount || 1) || 1)
  );
  const nextSalonEnabled =
    nextServiceMode === "kiosk" ? false : payload.salonEnabled !== false;
  const nextDeliveryEnabled = payload.deliveryEnabled === true;
  const nextStockEnabled = payload.stockEnabled !== false;
  const nextWaiterEnabled =
    nextSalonEnabled && payload.waiterEnabled !== false;
  const nextAiEnabled = payload.aiEnabled !== false;
  const nextWePedidosEnabled =
    nextDeliveryEnabled && payload.wePedidosEnabled === true;

  if (nextServiceMode !== "kiosk" && !nextSalonEnabled && !nextDeliveryEnabled) {
    throw new Error(
      "Activa al menos Salon o Delivery para crear la sucursal."
    );
  }

  const baseRow = {
    company_id: companyId,
    name: payload.name.trim(),
    phone: payload.phone?.trim() || null,
    address: payload.address?.trim() || null,
    description: payload.description?.trim() || null,
  };

  const attemptInsert = async (slug) =>
    supabase
      .from("branches")
      .insert({
        ...baseRow,
        slug,
      })
      .select("*")
      .single();

  let slug = createSlug(baseRow.name, "sucursal");
  let { data, error } = await attemptInsert(slug);

  if (error?.code === "23505") {
    slug = createSlug(baseRow.name, "sucursal", Date.now().toString().slice(-6));
    ({ data, error } = await attemptInsert(slug));
  }

  if (error) {
    throw normalizeError(error, "No se pudo crear la sucursal.");
  }

  const { error: branchSettingsError } = await supabase.from("branch_settings").upsert({
    branch_id: data.id,
    service_mode: nextServiceMode,
    salon_enabled: nextSalonEnabled,
    delivery_enabled: nextDeliveryEnabled,
    operational_setup_completed: true,
    stock_enabled: nextStockEnabled,
    waiter_enabled: nextWaiterEnabled,
    ai_enabled: nextAiEnabled,
    kiosk_menu_mode: nextKioskMenuMode,
    kiosk_station_count: nextKioskStationCount,
  });

  if (branchSettingsError) {
    throw normalizeError(
      branchSettingsError,
      "La sucursal se creo, pero no se pudo guardar su modalidad operativa."
    );
  }

  const { error: weSettingsError } = await supabase.from("we_branch_settings").upsert({
    branch_id: data.id,
    enabled: nextWePedidosEnabled,
    accepting_orders: true,
    price_level: 2,
    updated_at: new Date().toISOString(),
  });

  if (weSettingsError) {
    throw normalizeError(
      weSettingsError,
      "La sucursal se creo, pero no se pudo guardar la integracion con We! pedidos."
    );
  }

  const { error: assignMenuError } = await supabase.rpc("assign_shared_menu_to_branch", {
    p_branch_id: data.id,
  });

  if (assignMenuError && !isMissingMenuAssignmentRpcError(assignMenuError)) {
    throw normalizeError(
      assignMenuError,
      "La sucursal se creo, pero no se pudo vincular al menu compartido."
    );
  }

  if (nextServiceMode === "kiosk" && nextKioskMenuMode === "custom") {
    const { error: createCustomMenuError } = await supabase.rpc("create_custom_menu_for_branch", {
      p_branch_id: data.id,
      p_copy_from_current: false,
    });

    if (createCustomMenuError) {
      throw normalizeError(
        createCustomMenuError,
        "La sucursal kiosk se creo, pero no se pudo preparar su carta propia inicial."
      );
    }
  }

  return {
    ...data,
    serviceMode: nextServiceMode,
    salonEnabled: nextSalonEnabled,
    deliveryEnabled: nextDeliveryEnabled,
    operationalSetupCompleted: true,
    stockEnabled: nextStockEnabled,
    waiterEnabled: nextWaiterEnabled,
    aiEnabled: nextAiEnabled,
    wePedidosEnabled: nextWePedidosEnabled,
    kioskMenuMode: nextKioskMenuMode,
    kioskStationCount: nextKioskStationCount,
  };
};

export const deleteBranch = async (companyId, branchId) => {
  ensureSupabaseReady();

  if (!companyId || !branchId) {
    throw new Error("No encontramos la sucursal a eliminar.");
  }

  const { error } = await supabase
    .from("branches")
    .delete()
    .eq("company_id", companyId)
    .eq("id", branchId);

  if (error) {
    throw normalizeError(error, "No se pudo eliminar la sucursal.");
  }

  return branchId;
};

export const acceptPendingInvitationsForUser = async () => {
  ensureSupabaseReady();

  const { data, error } = await supabase.rpc("accept_my_pending_invitations");

  if (error) {
    if (isMissingRpcError(error)) {
      return {
        acceptedCount: 0,
        preferredBranchId: "",
        missingRpc: true,
      };
    }

    if (isMissingWaiterPermissionsError(error)) {
      throw new Error(
        "Falta aplicar waiter_permissions_patch.sql en Supabase para terminar de crear los permisos operativos de mozo en branch_user_access."
      );
    }

    throw normalizeError(error, "No se pudieron aplicar las invitaciones pendientes.");
  }

  const payload =
    data && typeof data === "object"
      ? data
      : {
          accepted_count: Number(data || 0),
        };

  return {
    acceptedCount: Number(payload.accepted_count || 0),
    preferredBranchId: payload.preferred_branch_id || "",
    companyId: payload.company_id || "",
    missingRpc: false,
  };
};

export const listCompanyInvitations = async (companyId) => {
  ensureSupabaseReady();

  if (!companyId) {
    return [];
  }

  const { data, error } = await supabase
    .from("user_invitations")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) {
    throw normalizeError(error, "No se pudieron cargar las invitaciones.");
  }

  return data || [];
};

export const createInternalUserInvitation = async (companyId, invitedBy, payload) => {
  ensureSupabaseReady();

  if (!companyId) {
    throw new Error("No encontramos una empresa activa para invitar usuarios internos.");
  }

  const { roleDefinition, email, branchId, displayName, branchPermissions } =
    buildInternalUserAccessPayload(payload);

  const row = {
    company_id: companyId,
    branch_id: roleDefinition.scope === "branch" ? branchId : null,
    email,
    role: "admin_limitado",
    workspace_role: roleDefinition.workspaceRole,
    display_name: displayName || null,
    invitation_token: createInvitationToken(),
    ...mapBranchPermissionsToColumns(branchPermissions),
    invited_by: invitedBy || null,
  };

  const { data, error } = await supabase
    .from("user_invitations")
    .insert(row)
    .select("*")
    .single();

  if (error) {
    if (isMissingInternalUserRolesV1PatchError(error)) {
      throw buildInternalUserRolesV1PatchError();
    }

    if (isMissingLimitedAdminVisibilityError(error)) {
      throw new Error(
        "Falta aplicar limited_admin_visibility_patch.sql en Supabase para guardar permisos de solo lectura en menu y stock."
      );
    }

    if (isMissingWaiterPermissionsError(error)) {
      throw new Error(
        "Falta aplicar waiter_permissions_patch.sql en Supabase para guardar permisos operativos del equipo."
      );
    }

    throw normalizeError(error, "No se pudo crear la invitacion.");
  }

  return {
    ...data,
    ...(await deliverInvitationSetupEmail(data)),
  };
};

export const createLimitedAdminInvitation = async (companyId, invitedBy, payload) =>
  createInternalUserInvitation(companyId, invitedBy, {
    ...payload,
    roleKey: "admin_limitado",
  });

export const createWaiterInvitation = async (companyId, invitedBy, payload) => {
  const waiterPermissions = mapWaiterPermissionsToBranchPermissions(
    payload.waiterPermissions,
    DEFAULT_WAITER_PERMISSIONS
  );

  return createInternalUserInvitation(companyId, invitedBy, {
    ...payload,
    roleKey: "mozo",
    fullName: payload.fullName,
    ...waiterPermissions,
  });
};

export const createKitchenInvitation = async (companyId, invitedBy, payload) =>
  createInternalUserInvitation(companyId, invitedBy, {
    ...payload,
    roleKey: "cocina",
  });

export const updateInternalUserInvitation = async (companyId, invitationId, payload) => {
  ensureSupabaseReady();

  if (!companyId || !invitationId) {
    throw new Error("No encontramos la invitacion que quieres actualizar.");
  }

  const { roleDefinition, email, branchId, displayName, branchPermissions } =
    buildInternalUserAccessPayload(payload);

  const row = {
    email,
    branch_id: roleDefinition.scope === "branch" ? branchId : null,
    workspace_role: roleDefinition.workspaceRole,
    display_name: displayName || null,
    ...mapBranchPermissionsToColumns(branchPermissions),
  };

  const { data, error } = await supabase
    .from("user_invitations")
    .update(row)
    .eq("company_id", companyId)
    .eq("id", invitationId)
    .select("*")
    .single();

  if (error) {
    if (isMissingInternalUserRolesV1PatchError(error)) {
      throw buildInternalUserRolesV1PatchError();
    }

    throw normalizeError(error, "No se pudo actualizar la invitacion.");
  }

  return data;
};

export const cancelInvitation = async (companyId, invitationId) => {
  ensureSupabaseReady();

  const { data, error } = await supabase
    .from("user_invitations")
    .update({
      status: "cancelled",
    })
    .eq("id", invitationId)
    .eq("company_id", companyId)
    .select("*")
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo cancelar la invitacion.");
  }

  return data;
};

export const deleteInvitation = async (companyId, invitationId) => {
  ensureSupabaseReady();

  const { error } = await supabase
    .from("user_invitations")
    .delete()
    .eq("id", invitationId)
    .eq("company_id", companyId);

  if (error) {
    throw normalizeError(error, "No se pudo eliminar la invitacion.");
  }
};

export const deactivateCompanyUser = async (companyId, userId) => {
  ensureSupabaseReady();

  if (!companyId || !userId) {
    throw new Error("No encontramos el usuario que quieres eliminar de la empresa.");
  }

  const { data: company, error: companyError } = await supabase
    .from("companies")
    .select("owner_user_id")
    .eq("id", companyId)
    .single();

  if (companyError) {
    throw normalizeError(companyError, "No se pudo validar el titular de la empresa.");
  }

  if (company?.owner_user_id && company.owner_user_id === userId) {
    throw new Error("El admin general titular de la empresa no se puede eliminar.");
  }

  const { data: companyUser, error: companyUserError } = await supabase
    .from("company_users")
    .update({
      active: false,
    })
    .eq("company_id", companyId)
    .eq("user_id", userId)
    .select("*")
    .single();

  if (companyUserError) {
    throw normalizeError(companyUserError, "No se pudo eliminar el usuario de la empresa.");
  }

  const { data: companyBranches, error: branchesError } = await supabase
    .from("branches")
    .select("id")
    .eq("company_id", companyId);

  if (branchesError) {
    throw normalizeError(branchesError, "No se pudieron cargar las sucursales de la empresa.");
  }

  const branchIds = (companyBranches || []).map((branch) => branch.id).filter(Boolean);

  if (branchIds.length) {
    const { error: accessError } = await supabase
      .from("branch_user_access")
      .update({
        active: false,
      })
      .eq("user_id", userId)
      .in("branch_id", branchIds);

    if (accessError) {
      throw normalizeError(accessError, "No se pudo desactivar el acceso del usuario en las sucursales.");
    }

    const { error: shiftsError } = await supabase
      .from("admin_shifts")
      .delete()
      .eq("user_id", userId)
      .in("branch_id", branchIds);

    if (shiftsError) {
      throw normalizeError(shiftsError, "No se pudieron limpiar los turnos del usuario eliminado.");
    }
  }

  return companyUser;
};

export const updateCompanyUserAccess = async (companyId, userId, payload) => {
  ensureSupabaseReady();

  if (!companyId || !userId) {
    throw new Error("No encontramos el usuario que quieres actualizar.");
  }

  const { data: company, error: companyError } = await supabase
    .from("companies")
    .select("owner_user_id")
    .eq("id", companyId)
    .single();

  if (companyError) {
    throw normalizeError(companyError, "No se pudo validar el titular de la empresa.");
  }

  if (company?.owner_user_id && company.owner_user_id === userId) {
    throw new Error("El usuario titular no se puede editar desde este flujo.");
  }

  const { roleDefinition, branchId, branchPermissions } =
    buildInternalUserAccessPayload(payload, {
      requireEmail: false,
      allowLegacyRole: true,
    });
  const nextActive = payload.active !== false;

  const { data: companyUser, error: companyUserError } = await supabase
    .from("company_users")
    .update({
      role: "admin_limitado",
      workspace_role: roleDefinition.workspaceRole,
      active: nextActive,
    })
    .eq("company_id", companyId)
    .eq("user_id", userId)
    .select("*")
    .single();

  if (companyUserError) {
    if (isMissingInternalUserRolesV1PatchError(companyUserError)) {
      throw buildInternalUserRolesV1PatchError();
    }

    throw normalizeError(companyUserError, "No se pudo actualizar el usuario interno.");
  }

  const { data: companyBranches, error: branchesError } = await supabase
    .from("branches")
    .select("id")
    .eq("company_id", companyId);

  if (branchesError) {
    throw normalizeError(branchesError, "No se pudieron cargar las sucursales de la empresa.");
  }

  const companyBranchIds = (companyBranches || [])
    .map((branch) => branch.id)
    .filter(Boolean);

  if (!companyBranchIds.length) {
    return companyUser;
  }

  if (roleDefinition.scope === "company") {
    const { error: deactivateAccessError } = await supabase
      .from("branch_user_access")
      .update({
        active: false,
      })
      .eq("user_id", userId)
      .in("branch_id", companyBranchIds);

    if (deactivateAccessError) {
      throw normalizeError(
        deactivateAccessError,
        "No se pudo limpiar el acceso puntual por sucursal del usuario."
      );
    }

    return companyUser;
  }

  const accessRow = {
    branch_id: branchId,
    user_id: userId,
    active: nextActive,
    ...mapBranchPermissionsToColumns(branchPermissions),
  };

  const { error: upsertAccessError } = await supabase
    .from("branch_user_access")
    .upsert(accessRow)
    .select("branch_id")
    .single();

  if (upsertAccessError) {
    throw normalizeError(
      upsertAccessError,
      "No se pudo guardar el acceso del usuario en la sucursal."
    );
  }

  const otherBranchIds = companyBranchIds.filter((currentBranchId) => currentBranchId !== branchId);

  if (otherBranchIds.length) {
    const { error: deactivateOtherBranchesError } = await supabase
      .from("branch_user_access")
      .update({
        active: false,
      })
      .eq("user_id", userId)
      .in("branch_id", otherBranchIds);

    if (deactivateOtherBranchesError) {
      throw normalizeError(
        deactivateOtherBranchesError,
        "No se pudo actualizar la sucursal asignada del usuario."
      );
    }
  }

  return companyUser;
};

export const listCompanyUsers = async (companyId, options = {}) => {
  ensureSupabaseReady();

  if (!companyId) {
    return [];
  }

  const includeInactive = options.includeInactive !== false;

  let query = supabase
    .from("company_users")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: true });

  if (!includeInactive) {
    query = query.eq("active", true);
  }

  const { data, error } = await query;

  if (error) {
    throw normalizeError(error, "No se pudieron cargar los usuarios de la empresa.");
  }

  const users = data || [];
  const userIds = users.map((companyUser) => companyUser.user_id).filter(Boolean);

  if (!userIds.length) {
    return users;
  }

  const { data: branchAccessRows, error: branchAccessError } = await supabase
    .from("branch_user_access")
    .select("*, branch:branches(id, company_id, name)")
    .in("user_id", userIds);

  if (branchAccessError) {
    throw normalizeError(
      branchAccessError,
      "No se pudieron cargar los accesos por sucursal del equipo."
    );
  }

  const { data: profiles, error: profilesError } = await supabase
    .from("user_profiles")
    .select("*")
    .in("user_id", userIds);

  if (profilesError) {
    if (isMissingUserProfilesTableError(profilesError)) {
      const branchAccessesByUserId = (branchAccessRows || []).reduce((map, row) => {
        if (!row?.user_id || row?.branch?.company_id !== companyId) {
          return map;
        }

        const currentRows = map.get(row.user_id) || [];
        currentRows.push({
          branchId: row.branch_id,
          branchName: row.branch?.name || "",
          active: row.active === true,
          permissions: normalizeBranchPermissions(row),
          createdAt: row.created_at || null,
        });
        map.set(row.user_id, currentRows);
        return map;
      }, new Map());

      return users.map((companyUser) => ({
        ...companyUser,
        profileFullName: "",
        profileDocumentId: "",
        profilePhone: "",
        branchAccesses: branchAccessesByUserId.get(companyUser.user_id) || [],
        primaryBranchId:
          branchAccessesByUserId.get(companyUser.user_id)?.find((row) => row.active)
            ?.branchId ||
          branchAccessesByUserId.get(companyUser.user_id)?.[0]?.branchId ||
          "",
        primaryBranchName:
          branchAccessesByUserId.get(companyUser.user_id)?.find((row) => row.active)
            ?.branchName ||
          branchAccessesByUserId.get(companyUser.user_id)?.[0]?.branchName ||
          "",
      }));
    }

    throw normalizeError(profilesError, "No se pudieron cargar los perfiles del equipo.");
  }

  const profilesByUserId = new Map(
    (profiles || []).map((profile) => [profile.user_id, profile])
  );
  const branchAccessesByUserId = (branchAccessRows || []).reduce((map, row) => {
    if (!row?.user_id || row?.branch?.company_id !== companyId) {
      return map;
    }

    const currentRows = map.get(row.user_id) || [];
    currentRows.push({
      branchId: row.branch_id,
      branchName: row.branch?.name || "",
      active: row.active === true,
      permissions: normalizeBranchPermissions(row),
      createdAt: row.created_at || null,
    });
    map.set(row.user_id, currentRows);
    return map;
  }, new Map());

  return users.map((companyUser) => {
    const profile = profilesByUserId.get(companyUser.user_id);
    const branchAccesses = branchAccessesByUserId.get(companyUser.user_id) || [];
    const primaryActiveBranch =
      branchAccesses.find((branchAccess) => branchAccess.active) || branchAccesses[0] || null;

    return {
      ...companyUser,
      profileFullName: profile?.full_name || "",
      profileDocumentId: profile?.document_id || "",
      profilePhone: profile?.phone || "",
      branchAccesses,
      primaryBranchId: primaryActiveBranch?.branchId || "",
      primaryBranchName: primaryActiveBranch?.branchName || "",
    };
  });
};

export const listBranchWaiters = async (companyId, branchId) => {
  ensureSupabaseReady();

  if (!companyId || !branchId) {
    return [];
  }

  const { data: accessRows, error: accessError } = await supabase
    .from("branch_user_access")
    .select("*")
    .eq("branch_id", branchId)
    .eq("can_manage_orders", true)
    .order("created_at", { ascending: true });

  if (accessError) {
    throw normalizeError(accessError, "No se pudo cargar el equipo operativo de la sucursal.");
  }

  const userIds = (accessRows || []).map((row) => row.user_id).filter(Boolean);

  if (!userIds.length) {
    return [];
  }

  const { data: companyUsers, error: companyUsersError } = await supabase
    .from("company_users")
    .select("*")
    .eq("company_id", companyId)
    .eq("workspace_role", "mozo")
    .in("user_id", userIds)
    .order("created_at", { ascending: true });

  if (companyUsersError) {
    if (isMissingWorkspaceRoleError(companyUsersError)) {
      throw new Error(
        "Falta aplicar el patch waiter_workspace_patch.sql en Supabase para listar mozos."
      );
    }

    throw normalizeError(companyUsersError, "No se pudo cargar la relacion del equipo.");
  }

  const waiterUserIds = (companyUsers || []).map((row) => row.user_id).filter(Boolean);

  if (!waiterUserIds.length) {
    return [];
  }

  const { data: profiles, error: profilesError } = await supabase
    .from("user_profiles")
    .select("*")
    .in("user_id", waiterUserIds);

  if (profilesError && !isMissingUserProfilesTableError(profilesError)) {
    throw normalizeError(profilesError, "No se pudieron cargar los perfiles de los mozos.");
  }

  const companyUsersById = new Map(
    (companyUsers || []).map((companyUser) => [companyUser.user_id, companyUser])
  );
  const profilesByUserId = new Map((profiles || []).map((profile) => [profile.user_id, profile]));

  return (accessRows || [])
    .filter((accessRow) => companyUsersById.has(accessRow.user_id))
    .map((accessRow) => {
      const companyUser = companyUsersById.get(accessRow.user_id);
      const profile = profilesByUserId.get(accessRow.user_id);

      return {
        userId: accessRow.user_id,
        branchId: accessRow.branch_id,
        branchAccessActive: accessRow.active === true,
        companyUserActive: companyUser?.active === true,
        createdAt: accessRow.created_at || companyUser?.created_at || null,
        profileFullName: profile?.full_name || "",
        profilePhone: profile?.phone || "",
        profileDocumentId: profile?.document_id || "",
        waiterPermissions: mapWaiterPermissionsFromDb(accessRow, {
          takeOrders: accessRow?.can_manage_orders === true,
          modifyOrders: accessRow?.can_manage_orders === true,
          cancelOrders: accessRow?.can_manage_orders === true,
          chargeOrders: accessRow?.can_manage_payments === true,
          refundOrders: accessRow?.can_manage_payments === true,
        }),
      };
    });
};

export const updateWaiterBranchAccess = async (branchId, userId, payload) => {
  ensureSupabaseReady();

  if (!branchId || !userId) {
    throw new Error("No encontramos el mozo o la sucursal para actualizar.");
  }

  const updatePayload =
    typeof payload === "object" && payload !== null
      ? {
          ...(payload.active === undefined ? {} : { active: Boolean(payload.active) }),
          ...(payload.waiterPermissions
            ? mapWaiterPermissionsToDb(
                payload.waiterPermissions,
                DEFAULT_WAITER_PERMISSIONS
              )
            : {}),
        }
      : {
          active: Boolean(payload),
        };

  const { data, error } = await supabase
    .from("branch_user_access")
    .update(updatePayload)
    .eq("branch_id", branchId)
    .eq("user_id", userId)
    .select("*")
    .single();

  if (error) {
    if (isMissingWaiterPermissionsError(error)) {
      throw new Error(
        "Falta aplicar waiter_permissions_patch.sql en Supabase para actualizar permisos operativos del mozo."
      );
    }

    throw normalizeError(error, "No se pudo cambiar el estado operativo del mozo.");
  }

  return data;
};

export const listAdminShifts = async (branchIds) => {
  ensureSupabaseReady();

  if (!branchIds?.length) {
    return [];
  }

  const { data, error } = await supabase
    .from("admin_shifts")
    .select("*, branch:branches(id, name)")
    .in("branch_id", branchIds)
    .order("created_at", { ascending: false });

  if (error) {
    throw normalizeError(error, "No se pudieron cargar los turnos.");
  }

  return data || [];
};

export const createAdminShift = async (payload) => {
  ensureSupabaseReady();

  if (!payload.branchId) {
    throw new Error("Selecciona una sucursal para el turno.");
  }

  if (!payload.userId) {
    throw new Error("Selecciona un administrador para asignar el turno.");
  }

  const { data, error } = await supabase
    .from("admin_shifts")
    .insert({
      branch_id: payload.branchId,
      user_id: payload.userId,
      day_of_week: Number(payload.dayOfWeek),
      start_time: payload.startTime,
      end_time: payload.endTime,
      notes: payload.notes?.trim() || null,
      active: true,
    })
    .select("*, branch:branches(id, name)")
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo crear el turno.");
  }

  return data;
};

export const deleteAdminShift = async (shiftId) => {
  ensureSupabaseReady();

  const { error } = await supabase.from("admin_shifts").delete().eq("id", shiftId);

  if (error) {
    throw normalizeError(error, "No se pudo eliminar el turno.");
  }

  return shiftId;
};
